import { Controller } from "@hotwired/stimulus"
//import { Datepicker } from "flowbite-datepicker"
import { DateRangePicker } from "flowbite-datepicker"
//import { Popover } from "flowbite"
import { createPopper } from "@popperjs/core"
import moment from "moment"

import { TimeRangePickerState } from "./time_range_picker/state"

export default class extends Controller {
  static targets = [
    "popper", "input", "info", "startAt", "stopAt", "rangePicker",
    "dateCalendar", "datePicker", "startPicker", "stopPicker"
  ]

  static values = {
    mode: String,
    start: String,
    stop: String,
    classes: Object,
    disabled: Boolean,
    debug: Boolean
  }

  connect() {
    const huntressData = window.huntressData || {}

    this.focused = false
    this.focusedItemIdx = -1
    this.mode = "range"
    this.state = new TimeRangePickerState(this.startValue, this.stopValue, huntressData.tzOffset)
    this.onStateChangedBound = ev => { this.onStateChanged(ev) }
    this.state.addEventListener("changed", this.onStateChangedBound)
    this.onNextUpdateCallbacks = []

    // Convert classes hash values to array of DOM tokens
    this.htmlClasses = Object.fromEntries(
      Object.keys(this.classesValue).map(k => [k, this.classesValue[k].split(" ")])
    )

    // Setup DateRangePicker from flowbite
    const [startDateVal, stopDateVal] = this.state.toDateValues()
    this.startPickerTarget.value = startDateVal
    this.stopPickerTarget.value = stopDateVal

    this.dateRangePicker = new DateRangePicker(this.element, {
      container: `#${this.dateCalendarTarget.id}`,
      format: "yyyy-mm-dd", // TODO localize format
      inputs: [this.startPickerTarget, this.stopPickerTarget],
    })

    // Reset some flowbite date picker tailwind classes so we can control position
    this.dateRangePicker.datepickers.forEach(({ picker }) => {
      picker.element.classList.remove("absolute", "pt-2")
      picker.element.querySelector(".shadow-lg").classList.remove("shadow-lg")
    })

    // Initial popout menu
    this.popper = createPopper(this.element, this.popperTarget, {
      placement: "bottom", modifiers: [{
        name: "offset",
        options: { offset: [0, 12] }
      }]
    })

    // Call DOM syncs and update UI by calling render methods
    this.syncFormFields()
    this.renderTextInput()
    this.renderInfoIcon()
  }

  disconnect() {
    this.dateRangePicker?.destroy()
    if (this.state && this.onStateChangedBound) {
      this.state.removeEventListener("changed", this.onStateChangedBound)
    }
    this.popper?.destroy()
  }

  // Sync the start and stop state to the hidden inputs which will be submitted
  // with any form including the time range picker component
  syncFormFields() {
    const [start, stop] = this.state.toInputValues()
    this.startAtTarget.value = start
    this.stopAtTarget.value = stop
  }

  // Sync state with the date range picker plugin
  syncDatePickers() {
    const [start, stop] = this.toDateValues()
    this.dateRangePicker.setDates(start, stop)
  }

  // Update the contents of the text input using the start and stop state
  renderTextInput() {
    this.inputTarget.value = this.state.isValid() ? this.state.toLocalizedString() : ""
  }

  // Update the contents of the info icon using the start and stop state
  renderInfoIcon() {
    if (this.state.isValid()) {
      const duration = this.state.toDurationString()

      this.infoTarget.parentElement.style.display = ""
      this.infoTarget.innerText = duration.humanize()
    } else {
      this.infoTarget.parentElement.style.display = "none"
    }
  }

  close() {
    this.toggleRangePickerItem(false)
    this.element.classList.remove(...this.htmlClasses.focus)
    this.popperTarget.classList.remove(...this.htmlClasses.popperFocus)

    if (this.focused) {
      this.dispatch("changed", { target: this.startAtTarget })
    }

    this.focused = false
    this.focusedItemIdx = -1

    if (this.mode === "calendar") {
      setTimeout(() => this.hideCalendar(), 250)
    } else if (this.mode === "keyboard") {
      this.mode = "range"
    }
  }

  showCalendar(picker = "start") {
    this.rangePickerTarget.classList.add("hidden")
    this.datePickerTarget.classList.remove("hidden")
    this.popperTarget.classList.add(...this.htmlClasses.popperCalendar)
    this.mode = "calendar"

    if (picker === "start") {
      this.dateRangePicker.datepickers[0].show()
      this.dateRangePicker.datepickers[1].hide()
    } else {
      this.dateRangePicker.datepickers[1].show()
      this.dateRangePicker.datepickers[0].hide()
    }
  }

  hideCalendar() {
    this.mode = "range"
    this.dateRangePicker.datepickers[0].hide()
    this.dateRangePicker.datepickers[1].hide()
    this.datePickerTarget.classList.add("hidden")
    this.rangePickerTarget.classList.remove("hidden")
    this.popperTarget.classList.remove(...this.htmlClasses.popperCalendar)
  }

  setFromRangePickerItem(item) {
    const { dataset } = item

    if (dataset.rangeAmount && dataset.rangeUnit) {
      this.onNextUpdateCallbacks.push(() => this.close())
      this.state.updateFromRelativeRangeOption(+dataset.rangeAmount, dataset.rangeUnit)
    }
  }

  toggleRangePickerItem(toggle) {
    if (this.focusedItemIdx === -1)
      return

    const item = this.rangePickerTarget.children[this.focusedItemIdx].children[0]

    if (item) {
      if (toggle) item.classList.add(...this.htmlClasses.itemFocus)
      else item.classList.remove(...this.htmlClasses.itemFocus)
    }
  }

  onStateChanged(ev) {
    this.log("onStateChanged", ev, this.state, this.onNextUpdateCallbacks.length)

    this.element.classList.remove(...this.htmlClasses.error)

    this.syncFormFields()
    this.renderInfoIcon()

    if (ev.detail && ev.detail.mode !== "relative") {
      this.renderTextInput()
    }

    let callback = this.onNextUpdateCallbacks.shift()

    while (callback) {
      callback()

      callback = this.onNextUpdateCallbacks.shift()
    }
  }

  onFocus() {
    if (this.hasDisabledValue && this.disabledValue) {
      return
    }

    if (!this.focused) {
      this.focused = true

      this.element.classList.add(...this.htmlClasses.focus)
      this.popperTarget.classList.add(...this.htmlClasses.popperFocus)
      this.inputTarget.setSelectionRange(0, this.inputTarget.value.length)
    }
  }

  // TODO implement text up/down arrow and track current text selection
  onSelectionChange(ev) {
    this.log("onSelectionChange", ev)
  }

  onValidateInput(ev) {
    const code = ev.keyCode || ev.which

    // Skip validating arrow keys or tab
    if (code === 37 || code === 38 || code === 39 || code === 40 || code === 9) {
      return
    }

    const valid = this.state.updateFromTextInput(this.inputTarget.value)

    if (this.mode === "range") {
      this.mode = "keyboard"
      this.toggleRangePickerItem(false)
      this.focusedItemIdx = -1
    }

    if (!valid) {
      this.element.classList.add(...this.htmlClasses.error)
      this.infoTarget.parentElement.style.display = "none"
    }
  }

  // UP key was pressed down
  onKeyDownUp() {
    if (this.mode !== "range") return

    if (this.focusedItemIdx > -1) {
      this.toggleRangePickerItem(false)
    }

    this.focusedItemIdx = this.focusedItemIdx > 0 ? this.focusedItemIdx - 1 : this.rangePickerTarget.children.length - 1
    this.toggleRangePickerItem(true)
  }

  // DOWN key was pressed down
  onKeyDownDown() {
    if (this.mode !== "range") return

    if (this.focusedItemIdx > -1) {
      this.toggleRangePickerItem(false)
    }

    this.focusedItemIdx = (this.focusedItemIdx + 1) % this.rangePickerTarget.children.length
    this.toggleRangePickerItem(true)
  }

  // ENTER key was pressed down, parse any relative input
  onKeyDownEnter(ev) {
    if (this.focusedItemIdx > -1) {
      if (this.focusedItemIdx + 1 === this.rangePickerTarget.children.length) {
        this.showCalendar()
        return
      }

      const item = this.rangePickerTarget.children[this.focusedItemIdx]

      if (item) {
        this.setFromRangePickerItem(item)
      }
    }

    this.renderTextInput(true)
    this.close()
    this.inputTarget.blur()
  }

  // TODO shift focus between text input and dropdown menu
  // onKeyDownTab(ev) {
  // this.log("onKeyDownTab", ev)
  // }

  // TODO global keypress on escape
  //onKeyPressEcape() {
  //this.close()
  //}

  onStartPickerChanged(ev) {
    this.log("onStartPickerChanged", ev)

    // Update start of range with the user's date selection
    const start = moment(ev.detail.date).utc()

    // Toggle to the stop picker
    this.showCalendar("stop")

    if (this.state.isStartDaySame(start)) {
      console.debug("onStartPickerChanged early return")
      return
    }

    this.state.updateStartDate(start)
  }

  onStopPickerChanged(ev) {
    this.log("onStopPickerChanged", ev)

    // Update stop moment object with the user's date selection
    const stop = moment(ev.detail.date).utc()

    if (this.state.isStopDaySame(stop)) {
      console.debug("onStopPickerChanged early return")
      return requestAnimationFrame(() => { this.close() })
    }

    this.state.updateStopDate(stop)

    requestAnimationFrame(() => { this.close() })
  }

  onClickRangeSelected(ev) {
    this.setFromRangePickerItem(ev.currentTarget)
  }

  onItemMouseMove(ev) {
    const item = ev.target.closest("li")

    if (!item) {
      return
    }

    if (this.focusedItemIdx > -1) {
      this.toggleRangePickerItem(false)
    }

    this.focusedItemIdx = [...this.rangePickerTarget.children].indexOf(item)

    if (this.focusedItemIdx > -1) {
      this.toggleRangePickerItem(true)
    }
  }

  onClickCalendarRange() {
    this.showCalendar()
  }

  onClickOutside(ev) {
    if (this.element.contains(ev.target) || !document.documentElement.contains(ev.target))
      return

    this.element.classList.remove(...this.htmlClasses.error)

    this.renderTextInput()
    this.close()
  }

  log(...rest) {
    if (this.hasDebugValue && this.debugValue) {
      console.log(...rest)
    }
  }
}
